import { ApolloQueryResult, gql } from '@apollo/client';
import { all, call, put, select, spawn, takeLatest } from 'redux-saga/effects';

import { AccountType, Address, ContactMethodLabels, Query, Role } from '@mfe/shared/schema-types';

import { resetScrubbedAddress } from '.';
import { processScrubResult } from '../disconnect';
import { clearError, setGeneralError } from '../error';
import { FetchWithErrorsQuery, graphqlQueryWithErrors } from '../graphql';
import { selectUserInfo, updateContactInfo, updateContactInfoSMB } from '../userInfo/userInfoSlice';
import {
  fetchScrubbedAddress as fetchScrubbedAddressAction,
  ScrubbedAddressState,
  selectScrubbedAddress,
  setCloseAfterReload,
  setEditingCard,
  setScrubbedAddress,
  setScrubbedAddressLoading,
  setShowScrubModal,
} from './scrubbedAddressSlice';
import {
  ScrubAddressProcessStatus,
  ScrubAddressRecommendation,
} from '../../../../platform/src/server/dataSources/PAL/types';
import { EditableCardType, ScrubAddressModalType } from './types';

export const GET_SCRUBBED_ADDRESS = gql`
  query getScrubbedAddress($address: GetScrubbedAddressInput!) {
    getScrubbedAddress(address: $address) {
      addressLines
      municipality
      region
      country
      postalCode
      countryCode
      processStatus
      recommendation
    }
  }
`;

export const parseScrubbedAddress = (
  data: Partial<ApolloQueryResult<Query>['data']>,
  secondAddressLine: string
): ScrubbedAddressState['scrubbedAddress'] | null => {
  if (!data || !data.getScrubbedAddress) return null;

  const apiResponse = data.getScrubbedAddress;

  const {
    addressLines,
    municipality = '',
    region = '',
    postalCode = '',
    country = '',
    countryCode,
    processStatus,
    recommendation,
  } = apiResponse;
  const [firstAddressLine = ''] = addressLines;

  const formattedAddress =
    processStatus !== ScrubAddressProcessStatus.Incorrect
      ? `${firstAddressLine} ${secondAddressLine} ${municipality}, ${region} ${postalCode} ${country}`
      : '';

  const rawAddress = {
    addressLines: [...addressLines, ...(secondAddressLine ? [secondAddressLine] : [])],
    municipality,
    region,
    postalCode,
    countryCode,
  };

  return {
    rawAddress,
    formattedAddress,
    processStatus: processStatus as ScrubAddressProcessStatus,
    recommendation: recommendation as ScrubAddressRecommendation,
  };
};

export function* fetchScrubbedAddress({ payload }: { type: string; payload: Address }) {
  const address = payload;
  yield put(clearError({}));

  try {
    const apiResponse: FetchWithErrorsQuery = yield call(graphqlQueryWithErrors, {
      query: GET_SCRUBBED_ADDRESS,
      variables: { address },
      fetchPolicy: 'no-cache',
    });

    yield put(resetScrubbedAddress());

    const { data, errors, runtimeError } = apiResponse;

    if (runtimeError || errors) {
      yield put(setScrubbedAddressLoading(false));
      yield put(setShowScrubModal(ScrubAddressModalType.AddressError));
      yield put(setGeneralError(runtimeError ?? errors));
      return;
    }

    const secondAddressLine = payload.addressLines[1];
    const scrubbedAddress = data ? parseScrubbedAddress(data, secondAddressLine) : null;

    if (scrubbedAddress) {
      yield put(setScrubbedAddress({ rawInput: payload, scrubbedAddress }));
      yield put(processScrubResult({ rawInput: payload, scrubbedAddress }));
    } else {
      yield put(
        setGeneralError({
          operation: 'getScrubbedAddress',
          message: ['getScrubbedAddress attempt failed.'],
        })
      );
    }
  } catch (error) {
    yield put(setScrubbedAddressLoading(false));
    yield put(setShowScrubModal(ScrubAddressModalType.AddressError));
    yield put(
      setGeneralError({
        operation: 'getScrubbedAddress',
        message: ['getScrubbedAddress attempt failed.'],
        details: error,
      })
    );
    yield put(resetScrubbedAddress());
  }
}

type PayloadType = {
  rawInput: Address;
  scrubbedAddress: ScrubbedAddressState['scrubbedAddress'];
};

export function* maybeSaveScrubbedAddress({ payload }: { type: string; payload: PayloadType }) {
  const { processStatus, recommendation } = payload.scrubbedAddress;
  const { editingCard, scrubbedAddress } = yield select(selectScrubbedAddress);

  // Disconnect flow does not set editingCard and requires explicit user confirmation
  // from the scrub modal before persisting address changes.
  if (editingCard === EditableCardType.None) {
    return;
  }

  const {
    userInfo: { accountType },
  } = yield select(selectUserInfo);

  if (
    processStatus === ScrubAddressProcessStatus.Verified ||
    (processStatus === ScrubAddressProcessStatus.Corrected && recommendation === ScrubAddressRecommendation.RECOMMEND)
  ) {
    yield put(setShowScrubModal(null));

    const labels = [];

    if (editingCard === EditableCardType.Billing) {
      labels.push(ContactMethodLabels.Invoice);
    }

    if (editingCard === EditableCardType.Shipping) {
      labels.push(ContactMethodLabels.Shipping);
    }

    const addresses = [
      {
        addressLines: scrubbedAddress.rawAddress.addressLines,
        countryCode: scrubbedAddress.rawAddress.countryCode,
        municipality: scrubbedAddress.rawAddress.municipality,
        postalCode: scrubbedAddress.rawAddress.postalCode,
        region: scrubbedAddress.rawAddress.region,
        labels,
      },
    ];

    if (accountType === AccountType.Residential) {
      yield put(setEditingCard(EditableCardType.None));
      yield put(setCloseAfterReload(true));
      yield put(updateContactInfo({ addresses }));
    } else {
      yield put(
        updateContactInfoSMB({
          role: Role.Customer,
          addresses,
        })
      );
    }

    return;
  }

  if (processStatus === ScrubAddressProcessStatus.Corrected) {
    return;
  }

  yield put(setShowScrubModal(ScrubAddressModalType.AddressError));
}

export function* watchScrubbedAddress() {
  yield spawn(function* () {
    yield takeLatest(fetchScrubbedAddressAction.type, fetchScrubbedAddress);
  });
  yield all([takeLatest(setScrubbedAddress.type, maybeSaveScrubbedAddress)]);
}
